import PrivateChannel from './PrivateChannel';

const PRESENCE_CHANNEL_NAME_PREFIX = 'presence-';
const CLIENT_EVENT_PREFIX = 'client-';

const TYPING_EVENT_NAME = 'typing';
const ONE_SEC = 1000; /* ms */

export default class PresenceChannel extends PrivateChannel {
    /**
     * @constructs [PresenceChannel]
     * @property {object} channel - Pusher API channel instance
     */
    constructor(channel) {
        super(channel);
        this.typingEventInterval = 5 * ONE_SEC;
        this.lastTriggeredTypingEventTime = new Date(0); // event has not been triggered yet

        this._bindClientEvent = this._bindClientEvent.bind(this);
        this._triggerClientEvent = this._triggerClientEvent.bind(this);
        this._unbindClientEvent = this._unbindClientEvent.bind(this);
        this.bindTypingEvent = this.bindTypingEvent.bind(this);
        this.triggerTypingEvent = this.triggerTypingEvent.bind(this);
        this.unbindTypingEvent = this.unbindTypingEvent.bind(this);
        this._canTriggerTypingEvent = this._canTriggerTypingEvent.bind(this);
    }

    static get channelPrefix() {
        return PRESENCE_CHANNEL_NAME_PREFIX;
    }

    /**
     * @property {string[]} channelNames - Channel names
     * @return {string[]} Channel names with prefixes
     */
    static addPrefix(channelNames) {
        return (channelNames ?? []).map((channelName) => `${PresenceChannel.channelPrefix}${channelName}`);
    }

    /**
     *
     * @param {string} eventName - client event raw name
     * @param {function} onEvent - callback function to be called when an event is received
     */
    _bindClientEvent(eventName, onEvent) {
        this.channel.bind(`${CLIENT_EVENT_PREFIX}${eventName}`, (data, metadata) => onEvent(data, metadata));
    }

    /**
     *
     * @param {string} eventName - client event raw name
     * @param {object} data - data to be sent to the event
     * @return {boolean} true if the event was successfully triggered, otherwise false
     */
    _triggerClientEvent(eventName, data) {
        return this.channel.trigger(`${CLIENT_EVENT_PREFIX}${eventName}`, data);
    }

    _unbindClientEvent(eventName) {
        this.channel.unbind(`${CLIENT_EVENT_PREFIX}${eventName}`);
    }

    /**
     *
     * @param {function} - typingCallback - callback function that will be called when this event is received
     */
    bindTypingEvent(typingCallback) {
        const clientEventCallback = (_, metadata) => {
            if (metadata.user_id !== this._memberId) {
                typingCallback(metadata.user_id);
            }
        };
        this._bindClientEvent(TYPING_EVENT_NAME, clientEventCallback);
    }

    /**
     * @return {boolean} true if the event was successfully triggered, otherwise false
     */
    triggerTypingEvent() {
        const currentTime = new Date();
        if (this._canTriggerTypingEvent(currentTime)) {
            this.lastTriggeredTypingEventTime = currentTime;
            return this._triggerClientEvent(TYPING_EVENT_NAME, {});
        }

        return false;
    }

    unbindTypingEvent() {
        this._unbindClientEvent(TYPING_EVENT_NAME);
    }

    _canTriggerTypingEvent(currentTime) {
        return ((currentTime - this.lastTriggeredTypingEventTime) > this.typingEventInterval);
    }

    get _memberId() {
        return String(this.channel.members.me.id);
    }
}
